/**
 * Operation-aware copy for the Upscale & Enhance feature.
 *
 * The feature runs one of three operations (EnhanceOperation): a resolution
 * `upscale`, a `restore` (= "Enhance": noise/blur cleanup), or `sdr-to-hdr`
 * (= "HDR" conversion). Surfaces should read the operation so labels say the
 * right thing instead of always "Upscale".
 *
 * The operation is persisted on `asset.upscaleParams.operation` (stamped when the
 * upscaled asset lands in Yjs). For legacy/job-less assets that predate that, we
 * fall back to the model: the Hyperion models are the SDR→HDR path; everything
 * else is treated as a plain upscale.
 */ import { useMemo } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import type { Asset, EnhanceOperation } from '@/types';
import { isHyperionVideoModel } from '@/types/shared';
import { isCameraMotionControlAsset, isImageAsset, isSetAsset, isVideoAsset } from '@/lib/assetUtils';
export function getDraftFamilyLabel(modelId: string | undefined): 'FLUX Draft' | 'Seedance Draft' | 'Draft' {
    if (modelId === 'flux-3-draft-enhance') return 'FLUX Draft';
    if (modelId?.startsWith('seedance-')) return 'Seedance Draft';
    return 'Draft';
}
/** Matches the canvas context-menu gate for starting an upscale. */ export function isCanvasUpscaleEligibleAsset(asset: Asset | null | undefined): asset is Asset {
    return Boolean(asset && asset.status === 'completed' && (asset.proxyUri || asset.uri) && !isSetAsset(asset) && !isCameraMotionControlAsset(asset) && (isVideoAsset(asset) || isImageAsset(asset)));
}
/**
 * English-only operation copy, for the few non-React string builders that can't
 * call a hook — the version-label cluster in `UpscaleOptionsModal`
 * (`getVersionLabel` → `getVersionDetailRows`) and the canvas generating overlay
 * (`getOverlayLabel`, an engine-sync callback). Those surfaces are not yet
 * internationalized at all, so English here keeps them self-consistent.
 *
 * React surfaces must use {@link useUpscaleOperationCopy} instead, which returns
 * translated copy. The literals are deliberately duplicated between the two:
 * next-intl's build-time extractor only collects `t('…')` calls bound to a
 * `useExtracted()` hook, so the translatable literals have to live inside the
 * hook below — a string interpolated from here into a caller's `t('{verb} …')`
 * would never be collected and would ship untranslated (the ENG-513 #5 bug).
 */ export function getUpscaleOperationNoun(operation: EnhanceOperation | undefined, model?: string): string {
    switch(operation){
        case 'restore':
            return 'Enhance';
        case 'sdr-to-hdr':
            return 'HDR';
        case 'draft-enhance':
            return `${getDraftFamilyLabel(model)} Enhance`;
        default:
            return 'Upscale';
    }
}
/** English present-participle verb. React surfaces should use {@link useUpscaleOperationCopy}. */ export function getUpscaleOperationVerb(operation: EnhanceOperation | undefined, model?: string): string {
    switch(operation){
        case 'restore':
            return 'Enhancing';
        case 'sdr-to-hdr':
            return 'Enhancing to HDR';
        case 'draft-enhance':
            return `Enhancing ${getDraftFamilyLabel(model)}`;
        default:
            return 'Upscaling';
    }
}
/**
 * Translated operation copy for React surfaces (badges, version labels, progress
 * verbs). Call at a component's top level, then use the returned `noun`/`verb` —
 * e.g. `t('{label} v{number}', { label: noun(op), number })`.
 *
 * The literal `t('…')` calls live here so next-intl's extractor collects them.
 */ export function useUpscaleOperationCopy(): {
    noun: (operation: EnhanceOperation | undefined, model?: string) => string;
    verb: (operation: EnhanceOperation | undefined, model?: string) => string;
} {
    const t = useTranslations$1();
    // Stable identities across renders so callers (incl. memoized canvas nodes) can
    // safely use the returned functions without busting memoization.
    return useMemo(()=>({
            noun: (operation: EnhanceOperation | undefined, model?: string)=>{
                switch(operation){
                    case 'restore':
                        return t("7EhXKK");
                    case 'sdr-to-hdr':
                        return t("ktdJFr");
                    case 'draft-enhance':
                        return `${getDraftFamilyLabel(model)} Enhance`;
                    default:
                        return t("InJVQX");
                }
            },
            verb: (operation: EnhanceOperation | undefined, model?: string)=>{
                switch(operation){
                    case 'restore':
                        return t("H04l1z");
                    case 'sdr-to-hdr':
                        return t("vfCcMC");
                    case 'draft-enhance':
                        return `Enhancing ${getDraftFamilyLabel(model)}`;
                    default:
                        return t("8tYiA0");
                }
            }
        }), [
        t
    ]);
}
/**
 * True when an asset's upscale output is a download-only EXR master (a frame
 * sequence that can't be previewed in the browser). Detected from the persisted
 * output-format tag (set at submit) or the master's .exr URI.
 */ export function isExrAsset(asset: Asset): boolean {
    const fmt = asset.upscaleParams?.outputFormat ?? '';
    return /exr/i.test(fmt) || /\.exr(\?|$)/i.test(asset.uri ?? '');
}
/**
 * True when an asset's video is ProRes — a professional finishing/mastering codec
 * that no browser can decode. Like EXR, Source-mode playback must offer a download
 * instead of handing the file to a <video> element (which renders black). Detected
 * from the probed codec, the persisted output tag, or a .mov URI.
 */ export function isProResAsset(asset: Asset): boolean {
    if (asset.codec?.toLowerCase() === 'prores') return true;
    return /prores/i.test(asset.upscaleParams?.outputFormat ?? '');
}
/** Best-known operation for a completed asset (persisted, else inferred from model). */ export function resolveAssetUpscaleOperation(asset: Asset): EnhanceOperation {
    const persisted = asset.upscaleParams?.operation;
    if (persisted) return persisted;
    const model = asset.upscaleModel ?? asset.upscaleParams?.model;
    if (model && isHyperionVideoModel(model)) return 'sdr-to-hdr';
    return 'upscale';
}
